import {Clone} from "js-vextensions";
import {GenerateUUID, observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, CheckBox, Column, Row, RowLR, Select, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {Slider, TextPlus} from "web-vcore";
import {Mod_TerrainMaterials} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainMaterials.js";
import {BLEND_MODES, LayerArg, MaterialLayer, MaterialRecipe, ParamValueInRecipe, PRIMITIVE_KINDS, RAMP_MAX_STOPS, type BlendMode} from "../../../../../../Terrain/Materials/@MaterialTypes.js";
import {getPrimitiveDef} from "../../../../../../Terrain/Materials/PrimitiveRegistry.js";
import {ResolvedRecipe, RESOLVER_DEPTH_CAP} from "../../../../../../Terrain/Materials/RecipeResolverCore.js";
import {GetProcMatRecipeLineage} from "../../../../../../Server/DB/mapObjects/ProcMatAccessors.js";
import {ShowMessageBox} from "react-vmessagebox";
import {CreateDerivedRecipe, CreateForkedRecipe} from "../TerrainMaterials_ModUI.js";
import {SpinnerForPercent} from "../../../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {DialogStyle, sliderFlex} from "../../../../../../Utils/UI/GlobalStyles.js";
import {ParamValueEditor} from "../TerrainParams/ParamValueEditor.js";

const splitAt = 180;

/** The full recipe editor: name, lineage, cost badge, param sliders, layer list, and the
 *  variant authoring controls (make variant / duplicate & detach). */
export const RecipeEditor = observer_mgl((props: {
	mod: Mod_TerrainMaterials;
	mapID: string;
	recipe: MaterialRecipe;
	recipeIndex: number;
	ChangeMod: (mutator: (mod: Mod_TerrainMaterials)=>void)=>void;
	onChange: (recipe: MaterialRecipe)=>void;
	onClone: ()=>void;
	onDelete: ()=>void;
})=>{
	const {mod, recipe, ChangeMod, onChange, onClone, onDelete, mapID} = props;
	const [expandedLayers, setExpandedLayers] = useState<Record<string, boolean>>({});

	const lineage = GetProcMatRecipeLineage(recipe.id, mapID);
	const resolved = lineage.resolved;
	const cost = editCostLabel(recipe, resolved);

	const ChangeRecipe = (mutator: (r: MaterialRecipe)=>any)=>{
		const newValue = Clone(recipe);
		mutator(newValue);
		onChange(newValue);
	};

	return (
		<Column mt={5} style={{background: "rgba(255,255,255,.15)", padding: 5, borderRadius: 5}}>
			<Row>
				<TextInput ml={0} style={{flex: 1}} value={recipe.name} onChange={val=>ChangeRecipe(r=>r.name = val)}/>
				<Button ml={5} p="3px 5px" text="JSON" onClick={()=>{
					let newJSON = JSON.stringify(recipe, undefined, "\t");
					const controller = ShowMessageBox({
						title: "JSON of recipe (editable)", cancelButton: true,
						okOnEnterKey: false, // disable Enter hotkey, so user can add lines to text-area
						message: ()=><Column style={DialogStyle({width: 600})}>
							<TextArea editable={true} style={{width: "100%", height: 500}} value={newJSON} onChange={val=>{
								newJSON = val;
								controller.UpdateUI();
							}}/>
						</Column>,
						onOK: ()=>{
							const dataFromDialog = JSON.parse(newJSON);
							ChangeRecipe(r=>{
								// clear all props on target recipe, then set new ones
								for (const key of Object.keys(r)) {
									delete r[key];
								}
								Object.assign(r, dataFromDialog);
							});
						}
					});
				}}/>
				<Button ml={5} mdIcon="content-copy" title="Clone this recipe" onClick={()=>{
					onClone();
				}}/>
				<Button ml={5} mdIcon="arrow-up" title="Move up" enabled={props.recipeIndex > 0} onClick={()=>{
					ChangeMod(a=>a.userRecipes.Move(a.userRecipes[props.recipeIndex], props.recipeIndex - 1));
				}}/>
				<Button ml={5} mdIcon="arrow-down" title="Move down" enabled={props.recipeIndex < mod.userRecipes.length - 1} onClick={()=>{
					ChangeMod(a=>a.userRecipes.Move(a.userRecipes[props.recipeIndex], props.recipeIndex + 1));
				}}/>
				<Button ml={5} mdIcon="delete" onClick={()=>{
					ShowMessageBox({
						title: "Delete recipe?", cancelButton: true,
						message: "Permanently delete this custom procedural-material recipe?",
						onOK: ()=>{
							onDelete();
						},
					});
				}}/>
			</Row>
			<Row mt={5}>
				<Text>Cost:</Text>
				{/* Cost-model badge (plan §9 Phase 7: surface whether an edit is free or recompiles). */}
				<Text ml={5} style={{fontSize: 11, opacity: cost.free ? 0.8 : 1, color: cost.free ? "#8f8" : "#f88"}}>{cost.label}</Text>
			</Row>
			<Row mt={5}>
				<Text>Base:</Text>
				<TextInput ml={5} style={{flex: 1}} value={recipe.baseRecipeID ?? ""}
					onChange={val=>ChangeRecipe(r=>r.baseRecipeID = val)}/>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					Inheritance lineage (root → this recipe).  A "variant" (extend) live-links to its base:
					base edits propagate.  A "fork" is a detached deep copy.
				`.AsMultiline(0)}>Lineage:</TextPlus>
				<Text ml={5} style={{fontSize: 12, opacity: 0.8}}>{lineage.chain.join(" → ")}{lineage.error ? ` (${lineage.error})` : ""}</Text>
			</Row>
			{lineage.error && <Row mt={2}><Text style={{color: "#f88", fontSize: 12}}>{lineage.error}</Text></Row>}
			{(resolved?.warnings ?? []).length > 0 && <Row mt={2}>
				<Text style={{color: "#fa8", fontSize: 12}}>{resolved!.warnings.join("; ")}</Text>
			</Row>}

			<RowLR mt={3} splitAt={splitAt}>
				<TextPlus info={`
					The lobe's width: 0 = tight glint, 1 = broad wash. Also the material's base relief response.
					Unset = inherit the base recipe's value.
				`.AsMultiline(0)}>Roughness:</TextPlus>
				<Slider min={0} max={1} step={.05} {...sliderFlex} value={resolved?.roughness ?? 0} onChange={val=>ChangeRecipe(r=>r.roughness = val)}/>
				<Spinner ml={5} placeholder={resolved?.roughness?.toString()}
					value={recipe.roughness} onChange={val=>ChangeRecipe(r=>r.roughness = val)}/>
				<Button ml={5} mdIcon="delete" enabled={recipe.roughness != null} onClick={()=>ChangeRecipe(r=>r.roughness = undefined)}/>
			</RowLR>

			<RowLR mt={3} splitAt={splitAt}>
				<TextPlus info={`
					Multiplier on the global specular lobe (0 = this material opts out of specular entirely).

					Unset = inherit the base recipe's value.
				`.AsMultiline(0)}>Spec strength:</TextPlus>
				<Slider min={0} max={1} step={.05} {...sliderFlex} value={resolved?.specStrength ?? 0} onChange={val=>ChangeRecipe(r=>r.specStrength = val)}/>
				<Spinner ml={5} placeholder={resolved?.specStrength?.toString()}
					value={recipe.specStrength} onChange={val=>ChangeRecipe(r=>r.specStrength = val)}/>
				<Button ml={5} mdIcon="delete" enabled={recipe.specStrength != null} onClick={()=>ChangeRecipe(r=>r.specStrength = undefined)}/>
			</RowLR>

			<RowLR mt={3} splitAt={splitAt}>
				<TextPlus info={`
					Ignores the terrain's own slope, shading from the material's detail normal only. (correct for
					level materials like water, and avoids glints that swim as the camera pans)

					Note: with a low global light altitude this leaves NO specular visible, since a level surface
					under a top-down view can't reach the highlight angle. Use "Light altitude override" to fix.

					Unset = inherit the base recipe's value.
				`.AsMultiline(0)}>Flat geo normal:</TextPlus>
				<CheckBox value={recipe.flatGeoNormal ?? "partial"} onChange={val=>ChangeRecipe(r=>r.flatGeoNormal = val)}/>
				<Text ml={5} style={{flex: 1}}>(resolved: {JSON.stringify(resolved?.flatGeoNormal ?? null)})</Text>
				<Button ml={5} mdIcon="delete" enabled={recipe.flatGeoNormal != null} onClick={()=>ChangeRecipe(r=>r.flatGeoNormal = undefined)}/>
			</RowLR>

			<RowLR mt={3} splitAt={splitAt}>
				<TextPlus info={`
					Shades this material as if the terrain-definition's global "Light altitude" had this value
					(degrees above the horizon). Azimuth, ambient and strength still come from the global rig.

					Why it exists: the view is top-down, so the specular half-vector sits (90 - altitude)/2
					degrees off vertical. At a low sun (eg. 30) that demands surface slopes far steeper than a
					water recipe's wave micro-slopes, so no highlight ever appears. Raising the altitude for
					just this material brings the highlight into reach without flattening the sun angle for the
					surrounding terrain.

					Unset = inherit the base recipe's value (if the whole chain is unset, the global value).
				`.AsMultiline(0)}>Light altitude override:</TextPlus>
				<Slider min={0} max={90} step={1} {...sliderFlex} value={resolved?.lightAltitudeOverride ?? 0} onChange={val=>ChangeRecipe(r=>r.lightAltitudeOverride = val)}/>
				<Spinner ml={5} placeholder={resolved?.lightAltitudeOverride?.toString()}
					value={recipe.lightAltitudeOverride} onChange={val=>ChangeRecipe(r=>r.lightAltitudeOverride = val)}/>
				<Button ml={5} mdIcon="delete" enabled={recipe.lightAltitudeOverride != null} onClick={()=>ChangeRecipe(r=>r.lightAltitudeOverride = undefined)}/>
			</RowLR>

			{/* Params — auto-generated from the RESOLVED schema (single source of truth). */}
			{resolved && resolved.params.length > 0 && <>
				<Row mt={5} style={{fontWeight: "bold"}}>Params:</Row>
				{resolved.params.map(p=>{
					const override = recipe.paramOverrides[p.key];
					return <ParamSliderRow key={p.key} param={p} resolvedRecipe={resolved}
						value={override}
						onChange={v=>{
							ChangeRecipe(r=>{
								//if (v === p.default || (Array.isArray(v) && v.length == 0)) delete r.paramOverrides[p.key];
								if (v === undefined) delete r.paramOverrides[p.key];
								else r.paramOverrides[p.key] = v;
							});
						}}/>;
				})}
			</>}

			{/* Layers (append/disable for extends; full editor for roots/forks). */}
			<Row mt={5} style={{fontWeight: "bold"}}>Layers:</Row>
			{(recipe.layers ?? []).map((layer, li)=>{
				const def = getPrimitiveDef(layer.primitive);
				const expanded = expandedLayers[layer.id];
				return (
					<Column key={layer.id} mt={3} style={{background: "rgba(255,255,255,.1)", padding: 3, borderRadius: 4}}>
						<Row>
							<CheckBox text="enabled" value={!recipe.disabledLayerIDs.includes(layer.id)} onChange={val=>ChangeRecipe(r=>{
								if (val) r.disabledLayerIDs = r.disabledLayerIDs.filter(id=>id != layer.id);
								else r.disabledLayerIDs = [...r.disabledLayerIDs, layer.id];
							})}/>
							<Text ml={5} style={{fontSize: 12}}>{layer.id}</Text>
							<Text ml={5} style={{fontSize: 12, opacity: 0.7}}>{def.implemented ? layer.primitive : `${layer.primitive} (not implemented)`}</Text>
							<Button ml="auto" p="1px 6px" text={expanded ? "−" : "+"} onClick={()=>setExpandedLayers({...expandedLayers, [layer.id]: !expanded})}/>
						</Row>
						{expanded && <>
							<RowLR mt={3} splitAt={splitAt}>
								<Text>Blend:</Text>
								<Select displayType="button bar" options={BLEND_MODES}
									value={layer.blend} onChange={val=>ChangeRecipe(r=>r.layers[li].blend = val as BlendMode)}/>
							</RowLR>
							{Object.entries(def.argSchema).map(([argKey, schema])=>{
								const cur = layer.args[argKey] ?? schema.default;
								return (
									<RowLR key={argKey} mt={2} splitAt={splitAt}>
										<Text style={{fontSize: 12, opacity: 0.8}}>{argKey}:</Text>
										{schema.type == "scalar"
											? <Spinner ml={5} style={{width: 70}}
												value={argValueForParam(cur, resolved, schema.default as number)}
												onChange={val=>{
													const proceed = ()=>ChangeRecipe(r=>r.layers[li].args[argKey] = val);
													const argWasParamControlled = recipe.layers[li].args[argKey]?.["ref"];
													if (!argWasParamControlled) {
														proceed();
													} else {
														ShowMessageBox({
															title: "Unlink from param?", cancelButton: true,
															message: `
																This argument is currently controlled by a parameter, but you are trying to set its value directly.
																Doing so necessitates the "unlinking" of this argument from the parameter. (dangling the parameter)
																Continue?
															`.AsMultiline(0),
															onOK: ()=>{
																proceed();
															}
														});
													}
												}}/>
											: <Text ml={5} style={{fontSize: 12, opacity: 0.6}}>{schema.type}</Text>}
									</RowLR>
								);
							})}
							<Row mt={3}>
								<Button ml="auto" p="1px 6px" text="delete layer" onClick={()=>ChangeRecipe(r=>{
									r.layers.splice(li, 1);
								})}/>
							</Row>
						</>}
					</Column>
				);
			})}
			{/* disabledLayerIDs — suppress base-chain layers (stale ids warn in the resolver, don't crash). */}
			{resolved && resolved.layers.length > 0 && <>
				{recipe.disabledLayerIDs.length > 0 && <Row mt={5} style={{fontWeight: "bold"}}>Disabled layers:</Row>}
				{recipe.disabledLayerIDs.map((lid, di)=>{
					// Candidates = still-enabled resolved layers; keep the current id in the options so a
					// stale/drifted id stays visible + changeable (the resolver warns on it).
					const options = resolved.layers.map(l=>l.id);
					if (!options.includes(lid)) options.push(lid);
					return (
						<Row key={di} mt={2}>
							<Select displayType="button bar" options={options} value={lid} onChange={val=>ChangeRecipe(r=>{
								r.disabledLayerIDs[di] = val as string;
							})}/>
							<Button ml={5} p="1px 6px" text="×" title="Re-enable this layer" onClick={()=>ChangeRecipe(r=>{
								r.disabledLayerIDs.splice(di, 1);
							})}/>
						</Row>
					);
				})}
				<Row mt={2}>
					<Button ml={0} p="1px 8px" text="+ disable layer" title="Suppress a layer from the base chain"
						onClick={()=>{
							const last = resolved.layers[resolved.layers.length - 1];
							if (last == null) return;
							ChangeRecipe(r=>{
								if (!r.disabledLayerIDs.includes(last.id)) r.disabledLayerIDs = [...r.disabledLayerIDs, last.id];
							});
						}}/>
				</Row>
			</>}
			<Row mt={4}>
				<Button ml={0} p="1px 8px" text="+ append layer" title="Append a new layer (extend-safe; appended layers keep their ids under base drift)"
					onClick={()=>{
						const layer = new MaterialLayer({
							id: `layer_${GenerateUUID().replace(/-/g, "").slice(0, 6)}`,
							primitive: PRIMITIVE_KINDS[1] as typeof layer.primitive, // fbm
							blend: "over",
						});
						ChangeRecipe(r=>r.layers = [...r.layers, layer]);
					}}/>
				{recipe.baseRecipeID != null && <>
					<Button ml={5} p="1px 8px" text="Detach (fork)" title="Deep-copy the resolved recipe into a new root (no live link). Structural edits become unrestricted."
						onClick={()=>{
							if (resolved == null) return;
							ShowMessageBox({
								title: "Mutate into fork?", cancelButton: true,
								message: `
									Proceeding will unlink this recipe from its base and duplicate all its fields.
									(with its current overrides baked into the new values)
								`.AsMultiline(0),
								onOK: ()=>{
									const fork = CreateForkedRecipe(resolved);
									onChange(fork); // replace in the mod (root now)
								},
							})
						}}/>
				</>}
			</Row>
		</Column>
	);
});

/** Resolve the per-param VALUE a layer arg references (param default after overrides), for the
 *  auto-generated slider value.  `arg` may be a literal or a ParamRef. */
function argValueForParam(arg: LayerArg, resolved: ResolvedRecipe | null, fallback: number): number {
	if (arg != null && typeof arg == "object" && !Array.isArray(arg) && "ref" in arg && resolved) {
		const p = resolved.params.find(p=>p.key == (arg as {ref: string}).ref);
		if (p && typeof p.default == "number") return p.default;
	}
	if (typeof arg == "number") return arg;
	return fallback;
}

/** The cost-model badge: whether an edit is free (params-only → same program) or triggers a
 *  recompile (layer change).  A recipe with a base (extend) whose edits are params-only is FREE. */
function editCostLabel(recipe: MaterialRecipe, resolved: ResolvedRecipe | null): {label: string; free: boolean} {
	// An extend recipe whose only deltas are paramOverrides/layerArgOverrides/disabledLayerIDs
	// (i.e. no append/remove layers) is free: the resolved structure is the base's + overrides.
	if (recipe.baseRecipeID != null && recipe.layers.length == 0) return {label: "params-only — free (same program)", free: true};
	if (recipe.layers.length > 0) return {label: "layer change — recompiles", free: false};
	return {label: "built-in (params editable, free)", free: true};
}

/** One row of the auto-generated param editor for a resolved recipe — a thin adapter over the
 *  SHARED {@link ParamValueEditor} (kind-dispatching).  The `LayerArgsThatUse` readout is
 *  material-specific (scenery params have no layer args), so it is folded into `info` here. */
export const ParamSliderRow = observer_mgl((props: {
	param: ResolvedRecipe["params"][number];
	resolvedRecipe: ResolvedRecipe | null;
	value: ParamValueInRecipe;
	onChange: (v: ParamValueInRecipe)=>void;
})=>{
	const {param, resolvedRecipe, value, onChange} = props;
	const info = `
		Parameter key: ${param.key}
		Default value: ${param.default}
		Min: ${param.min}
		Max: ${param.max}
		LayerArgsThatUse: ${
			(()=>{
				if (!resolvedRecipe) return "none";
				const uses: string[] = [];
				for (const layer of resolvedRecipe.layers) {
					for (const [argKey, argVal] of Object.entries(layer.args)) {
						if (typeof argVal == "object" && argVal != null && argVal["ref"] == param.key) {
							uses.push(`${layer.id}:${argKey}`);
						}
					}
				}
				return uses.length > 0 ? uses.join(", ") : "none";
			})()
		}
	`.AsMultiline(0);
	// Map ResolvedParam → ParamEditData.  `fallback` = the param's RESOLVED default (root +
	// intermediate + any leaf override) — correct as the inherit fallback here because when `value`
	// is inherited the current recipe has no override of its own to exclude (ResolveRecipe folds
	// paramOverrides into `default`, unlike scenery's separate `value`/`fallback`).
	return <ParamValueEditor param={{key: param.key, label: param.label, kind: param.kind, min: param.min, max: param.max, fallback: param.default}} value={value} onChange={onChange} info={info}/>;
});